import { Tooltip } from '@mui/material'
import {
  useMfrrCmBids,
  useMfrrConfig,
  useMfrrDeliveredBids,
  useMfrrMarketResults,
  useMfrrPublishedBids,
  usePublishMfrrCmBidsMutation,
  useSubmitMfrrCmBidsMutation,
  useUnpublishMfrrCmBidsMutation,
  useUpdateMfrrCmBidsMutation,
} from 'api/mfrr/mfrr.api'
import type { EnabledMfrrProductionUnit, MfrrBid } from 'api/mfrr/mfrr.types'
import { type ReactNode, useMemo, useReducer, useState, useSyncExternalStore } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import { colors } from 'styles/variables'
import Card from 'ui/atoms/Card/Card'
import NewButton from 'ui/atoms/NewButton/NewButton'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import ConfirmDialog from 'ui/components/ConfirmDialog/ConfirmDialog'
import InfoBanner from 'ui/components/InfoBanner/InfoBanner'
import SelectButton from 'ui/components/SelectButton/SelectButton'
import TooltipIconButton from 'ui/components/TooltipIconButton/TooltipIconButton'
import UiConfig from 'ui/uiConfig/UiConfig'
import Datetime from 'utils/datetime/datetime'
import { capitalize } from 'utils/stringUtils'
import {
  getCurrentMinuteMarker,
  hasEAMMarketClosedForDeliveryRow,
  isMfrrDateTooFarInFuture,
  subscribeToMinuteBoundary,
} from '../MFRRTradingDeadlineCounter/MFRRTradingDeadlineCounter.helper'
import { MfrrBidTable } from '../MfrrBidTable/MfrrBidTable'
import {
  EAM_QUARTER_MINUTES,
  EAM_ROW_COUNT,
  EMPTY_MFRR_BIDS,
  getMfrrQuarterIndex,
  mapMfrrBidsToRowValues,
} from '../MfrrBidTable/MfrrBidTable.helper'
import type {
  EamCopyFailureReason,
  EamCopyFromSource,
  MfrrBidTableEligibility,
  MfrrEamMarketResultViolation,
} from '../MfrrBidTable/MfrrBidTable.types'
import {
  getMfrrEamMarketResultViolations,
  getMfrrMarketResultVolumesByUnit,
} from '../MfrrBidTable/MfrrMarketResults.helper'
import { MfrrLastDeliveredStatus } from '../MfrrLastDeliveredStatus/MfrrLastDeliveredStatus'
import {
  getCanEmptyMfrrTable,
  getEffectiveMfrrSavedBids,
  getLatestMfrrBidsPerSlot,
  getMfrrBidActionAvailability,
  getMfrrBidStatusInfo,
  getMfrrDeliveryWindowEnd,
  mapMfrrBidIdsForUpdate,
  normalizeMfrrBidsForComparison,
} from '../MRFFCMTrading/MFRRCMTrading.helper'
import styles from '../MRFFCMTrading/MFRRCMTrading.module.less'
import {
  getEamCopyFromFailureMessageKey,
  mapMfrrBidsForEamCopy,
  mapMfrrMarketResultsForEamCopy,
} from './MFRREAMTrading.helper'
import { MfrrLockWarningTable } from './MfrrLockWarningTable'

const EAM_MARKET = 'eam' as const

type EamDialogState = {
  unlockConfirmOpen: boolean
  emptyTableConfirmOpen: boolean
  clearEditableRowsSignal: number | undefined
}

type EamDialogAction =
  | { type: 'openUnlockConfirm' }
  | { type: 'closeUnlockConfirm' }
  | { type: 'openEmptyTableConfirm' }
  | { type: 'closeEmptyTableConfirm' }
  | { type: 'incrementClearSignal' }

function eamDialogReducer(state: EamDialogState, action: EamDialogAction): EamDialogState {
  switch (action.type) {
    case 'openUnlockConfirm':
      return { ...state, unlockConfirmOpen: true }
    case 'closeUnlockConfirm':
      return { ...state, unlockConfirmOpen: false }
    case 'openEmptyTableConfirm':
      return { ...state, emptyTableConfirmOpen: true }
    case 'closeEmptyTableConfirm':
      return { ...state, emptyTableConfirmOpen: false }
    case 'incrementClearSignal':
      return {
        ...state,
        emptyTableConfirmOpen: false,
        clearEditableRowsSignal: (state.clearEditableRowsSignal ?? 0) + 1,
      }
    default:
      return state
  }
}

const INITIAL_EAM_DIALOG_STATE: EamDialogState = {
  unlockConfirmOpen: false,
  emptyTableConfirmOpen: false,
  clearEditableRowsSignal: undefined,
}

type MfrrLockFeedback = {
  violations: MfrrEamMarketResultViolation[]
  highlightedRowIndices: Set<number>
}

const NO_LOCK_FEEDBACK: MfrrLockFeedback = { violations: [], highlightedRowIndices: new Set() }

type MFRREAMTradingProps = {
  units: EnabledMfrrProductionUnit[]
  cmUnits: EnabledMfrrProductionUnit[]
  systemId: number | undefined
  dateStartTime: ISODateTime
  hasConfigError: boolean
  isConfigLoading: boolean
}

export function MFRREAMTrading({
  units,
  cmUnits,
  systemId,
  dateStartTime,
  hasConfigError,
  isConfigLoading,
}: MFRREAMTradingProps): ReactNode {
  const { t } = useTranslation()
  const { hasAccess } = useAuth()

  const { data: mfrrConfig } = useMfrrConfig(systemId)

  const dateEndTime = getMfrrDeliveryWindowEnd(dateStartTime)

  const { data: savedBids = EMPTY_MFRR_BIDS, isPending: isBidsLoading } = useMfrrCmBids({
    systemId,
    market: EAM_MARKET,
    startTime: dateStartTime,
    endTime: dateEndTime,
  })

  const { data: publishedBids = EMPTY_MFRR_BIDS } = useMfrrPublishedBids({
    systemId,
    market: EAM_MARKET,
    startTime: dateStartTime,
    endTime: dateEndTime,
  })
  const { data: deliveredBids = EMPTY_MFRR_BIDS } = useMfrrDeliveredBids({
    systemId,
    market: EAM_MARKET,
    startTime: dateStartTime,
    endTime: dateEndTime,
  })
  const { data: cmBidsForEamCopy = [], isPending: isCmBidsForEamCopyPending } = useMfrrCmBids({
    systemId,
    market: 'cm',
    startTime: dateStartTime,
    endTime: dateEndTime,
  })
  const { data: cmMarketResultsForEamCopy = [], isPending: isCmMarketResultsForEamCopyPending } = useMfrrMarketResults({
    systemId,
    market: 'cm',
    startTime: dateStartTime,
    endTime: dateEndTime,
  })

  // EAM bids are checked against the volumes already accepted on the CM market.
  const { data: cmMarketResults = [] } = useMfrrMarketResults({
    systemId,
    market: 'cm',
    startTime: dateStartTime,
    endTime: dateEndTime,
  })

  const cmBidsForEamCopyWithMappedUnits = useMemo(() => {
    return mapMfrrBidsForEamCopy(cmBidsForEamCopy, cmUnits, units)
  }, [cmBidsForEamCopy, cmUnits, units])
  const cmMarketResultsForEamCopyWithMappedUnits = useMemo(() => {
    return mapMfrrMarketResultsForEamCopy(cmMarketResultsForEamCopy, cmUnits, units)
  }, [cmMarketResultsForEamCopy, cmUnits, units])

  const hasEAMDecisionSupportGraph = hasAccess({ submodule: 'mfrr_eam_decision_support_graph', module: 'mfrrtrading' })
  const latestSavedBids = useMemo(() => getLatestMfrrBidsPerSlot(savedBids), [savedBids])
  const initialValues = useMemo(
    () => mapMfrrBidsToRowValues(latestSavedBids, dateStartTime, units, EAM_ROW_COUNT, EAM_MARKET),
    [latestSavedBids, dateStartTime, units]
  )

  const marketResultVolumesByUnit = useMemo(
    () => getMfrrMarketResultVolumesByUnit(cmMarketResults, units, dateStartTime),
    [cmMarketResults, units, dateStartTime]
  )

  const submitMutation = useSubmitMfrrCmBidsMutation()
  const updateMutation = useUpdateMfrrCmBidsMutation()
  const publishMutation = usePublishMfrrCmBidsMutation()
  const unpublishMutation = useUnpublishMfrrCmBidsMutation()

  const [currentPayload, setCurrentPayload] = useState<MfrrBid[]>([])
  const [eligibility, setEligibility] = useState<MfrrBidTableEligibility>({ canSubmit: true, disabledReason: '' })
  const [dialogState, dispatchDialogAction] = useReducer(eamDialogReducer, INITIAL_EAM_DIALOG_STATE)
  const [retainedUnlockValues, setRetainedUnlockValues] = useState<
    { date: ISODateTime; values: Array<Record<string, string>> } | undefined
  >(undefined)
  const tableInitialValues = retainedUnlockValues?.date === dateStartTime ? retainedUnlockValues.values : initialValues
  const [lockFeedback, setLockFeedback] = useState<MfrrLockFeedback & { date: ISODateTime }>({
    date: dateStartTime,
    ...NO_LOCK_FEEDBACK,
  })
  const { violations: lockWarningViolations, highlightedRowIndices } =
    lockFeedback.date === dateStartTime ? lockFeedback : NO_LOCK_FEEDBACK

  const clearLockFeedback = () => setLockFeedback({ date: dateStartTime, ...NO_LOCK_FEEDBACK })

  const handleSaveSuccess = () => {
    clearLockFeedback()
    setRetainedUnlockValues((previous) =>
      previous?.date === dateStartTime
        ? {
            date: dateStartTime,
            values: mapMfrrBidsToRowValues(
              currentPayload.map((bid) => ({ ...bid, production_unit_id: bid.production_unit_id ?? null })),
              dateStartTime,
              units,
              EAM_ROW_COUNT,
              EAM_MARKET
            ),
          }
        : previous
    )
  }

  const [eamCopyFromSignal, setEamCopyFromSignal] = useState<{ id: number; source: EamCopyFromSource } | undefined>(
    undefined
  )
  const minuteMarker = useSyncExternalStore(subscribeToMinuteBoundary, getCurrentMinuteMarker, getCurrentMinuteMarker)
  const now = minuteMarker * 60_000
  const isLocked = latestSavedBids.some(
    (bid) =>
      bid.status === 'published' &&
      !hasEAMMarketClosedForDeliveryRow(
        bid.start_time,
        now,
        mfrrConfig?.eam_gate_closure_minutes_before,
        mfrrConfig?.eam_input_margin_closure_time
      )
  )
  const hasDraftBids = latestSavedBids.length > 0 && !isLocked
  const hasDraftBidsForDeliveredDate = hasDraftBids && deliveredBids.length > 0

  const currentBidsSignature = useMemo(() => normalizeMfrrBidsForComparison(currentPayload), [currentPayload])
  const savedBidsSignature = useMemo(
    () =>
      normalizeMfrrBidsForComparison(
        getEffectiveMfrrSavedBids(latestSavedBids, publishedBids, (startTime) =>
          hasEAMMarketClosedForDeliveryRow(
            startTime,
            now,
            mfrrConfig?.eam_gate_closure_minutes_before,
            mfrrConfig?.eam_input_margin_closure_time
          )
        )
      ),
    [
      latestSavedBids,
      publishedBids,
      now,
      mfrrConfig?.eam_gate_closure_minutes_before,
      mfrrConfig?.eam_input_margin_closure_time,
    ]
  )
  const hasLocalChanges = currentBidsSignature !== savedBidsSignature

  const isEamDeadlinePassed = useMemo(() => {
    return Array.from({ length: EAM_ROW_COUNT }, (_unused, rowIndex) => {
      const rowStartTime = Datetime.addMinutes(dateStartTime, rowIndex * EAM_QUARTER_MINUTES)
      return hasEAMMarketClosedForDeliveryRow(
        rowStartTime,
        now,
        mfrrConfig?.eam_gate_closure_minutes_before,
        mfrrConfig?.eam_input_margin_closure_time
      )
    }).every((isRowClosed) => isRowClosed)
  }, [dateStartTime, mfrrConfig?.eam_gate_closure_minutes_before, mfrrConfig?.eam_input_margin_closure_time, now])

  const isSaving = submitMutation.isPending || updateMutation.isPending
  const { canSave, canLock } = getMfrrBidActionAvailability({
    isBidsLoading,
    isLocked,
    hasDraftBids,
    eligibilityCanSubmit: eligibility.canSubmit,
    hasSystemId: systemId !== undefined,
    payloadCount: currentPayload.length,
    hasLocalChanges,
    isSaving,
    isPublishing: publishMutation.isPending,
  })
  const isDateTooFarInFuture = isMfrrDateTooFarInFuture(dateStartTime, now, mfrrConfig?.eam_max_bid_days_ahead)
  const canSaveForSelectedDate = canSave && !isDateTooFarInFuture
  const canLockForSelectedDate = canLock && !isDateTooFarInFuture
  const hasEditableEamRows = useMemo(() => {
    return Array.from({ length: EAM_ROW_COUNT }, (_unused, rowIndex) => {
      const rowStartTime = Datetime.addMinutes(dateStartTime, rowIndex * EAM_QUARTER_MINUTES)
      return !hasEAMMarketClosedForDeliveryRow(
        rowStartTime,
        now,
        mfrrConfig?.eam_gate_closure_minutes_before,
        mfrrConfig?.eam_input_margin_closure_time
      )
    }).some(Boolean)
  }, [dateStartTime, mfrrConfig?.eam_gate_closure_minutes_before, mfrrConfig?.eam_input_margin_closure_time, now])

  const hasEditableValues = useMemo(() => {
    return currentPayload.some(
      (bid) =>
        (bid.volume !== 0 || bid.price !== 0) &&
        !hasEAMMarketClosedForDeliveryRow(
          bid.start_time,
          now,
          mfrrConfig?.eam_gate_closure_minutes_before,
          mfrrConfig?.eam_input_margin_closure_time
        )
    )
  }, [currentPayload, mfrrConfig?.eam_gate_closure_minutes_before, mfrrConfig?.eam_input_margin_closure_time, now])

  const canEmptyTable = getCanEmptyMfrrTable({
    isBidsLoading,
    isLocked,
    hasUnits: units.length > 0,
    hasEditableValues,
  })

  const canEmptyEamTable = canEmptyTable && hasEditableEamRows && !isDateTooFarInFuture
  const canShowActionButtons = hasEditableEamRows && !isDateTooFarInFuture
  const selectedCopySource = eamCopyFromSignal?.source
  const copySourceBidsForEam =
    selectedCopySource === 'cm-results' ? cmMarketResultsForEamCopyWithMappedUnits : cmBidsForEamCopyWithMappedUnits
  const isCopySourceBidsForEamPending =
    selectedCopySource === 'cm-results' ? isCmMarketResultsForEamCopyPending : isCmBidsForEamCopyPending

  const handleSaveClick = () => {
    if (systemId === undefined) {
      return
    }

    const payload = { system_id: systemId, bids: currentPayload }

    if (!hasDraftBids) {
      submitMutation.mutate(
        { payload },
        {
          onSuccess: handleSaveSuccess,
        }
      )
      return
    }

    const payloadWithIds = {
      system_id: systemId,
      bids: mapMfrrBidIdsForUpdate(currentPayload, latestSavedBids),
    }

    updateMutation.mutate(
      { payload: payloadWithIds },
      {
        onSuccess: handleSaveSuccess,
      }
    )
  }

  const handleLockClick = () => {
    if (systemId === undefined) {
      return
    }

    const marketResultViolations = getMfrrEamMarketResultViolations(
      currentPayload,
      dateStartTime,
      marketResultVolumesByUnit
    )

    if (marketResultViolations.length > 0) {
      setLockFeedback({ date: dateStartTime, violations: marketResultViolations, highlightedRowIndices: new Set() })
      return
    }

    publishBids()
  }

  const publishBids = () => {
    if (systemId === undefined) {
      return
    }

    clearLockFeedback()
    publishMutation.mutate(
      {
        payload: {
          system_id: systemId,
          market: EAM_MARKET,
          start_time: dateStartTime,
          end_time: dateEndTime,
        },
      },
      {
        onSuccess: () => setRetainedUnlockValues(undefined),
      }
    )
  }

  const handleCancelAndMarkRows = () => {
    setLockFeedback({
      date: dateStartTime,
      violations: [],
      highlightedRowIndices: new Set(
        lockWarningViolations.map((violation) => getMfrrQuarterIndex(violation.startTime, dateStartTime))
      ),
    })
  }

  const handleUnlockClick = () => {
    dispatchDialogAction({ type: 'openUnlockConfirm' })
  }

  const handleEmptyTableClick = () => {
    dispatchDialogAction({ type: 'openEmptyTableConfirm' })
  }

  const handleUnlockConfirm = () => {
    dispatchDialogAction({ type: 'closeUnlockConfirm' })

    if (systemId === undefined) {
      return
    }

    setRetainedUnlockValues({ date: dateStartTime, values: initialValues })
    unpublishMutation.mutate({
      payload: {
        system_id: systemId,
        market: EAM_MARKET,
        start_time: dateStartTime,
        end_time: dateEndTime,
      },
    })
  }

  const handleEmptyTableConfirm = () => {
    dispatchDialogAction({ type: 'incrementClearSignal' })
  }

  const handleCopyFromSelection = (source: EamCopyFromSource) => {
    setEamCopyFromSignal((previous) => ({
      id: (previous?.id ?? 0) + 1,
      source,
    }))
  }

  const handleCopyFromFailure = (reason: EamCopyFailureReason) => {
    alertStore.error(t(getEamCopyFromFailureMessageKey(reason)))
  }
  const bidStatusInfo = getMfrrBidStatusInfo(latestSavedBids, isLocked)
  const localDate = Datetime.toLocalTime(dateStartTime, 'onlyDate')
  const todayDate = Datetime.toLocalTime(Datetime.getTodayDate().startTime, 'onlyDate')
  const tomorrowDate = Datetime.toLocalTime(Datetime.getTomorrowDate().startTime, 'onlyDate')
  const titleDate =
    localDate === todayDate
      ? `${t('Today')} ${localDate}`
      : localDate === tomorrowDate
        ? `${t('Tomorrow')} ${localDate}`
        : `${capitalize(Datetime.toLocalTime(dateStartTime, 'shortDayText'))} ${localDate}`

  return (
    <>
      {hasEAMDecisionSupportGraph && (
        <Card title={t('EAM Price Decision Support')} collapsible rememberCollapsed>
          <UiConfig
            type="mfrr_eam_decision_support_graph"
            datasetStartTime={dateStartTime}
            datasetEndTime={dateEndTime}
          />
        </Card>
      )}
      <Card title={t('Place bids for EAM ({{date}})', { date: titleDate })}>
        {canShowActionButtons && (
          <div className={styles.MFRRCMTrading_TitleAction}>
            {!isLocked && canEmptyEamTable && (
              <NewButton onClick={handleEmptyTableClick} label={t('Clear table')} variant="cancel" />
            )}
            {!isLocked && (
              <SelectButton
                menuItems={[
                  {
                    label: t('Locked CM bids'),
                    onClick: () => handleCopyFromSelection('locked-cm-bids'),
                  },
                  {
                    label: t('CM results'),
                    onClick: () => handleCopyFromSelection('cm-results'),
                  },
                ]}
              >
                {t('Copy CM volume')}
              </SelectButton>
            )}

            {!isLocked && (
              <Tooltip title={!canSaveForSelectedDate ? t('No changes to save') : ''} arrow>
                <span>
                  <NewButton
                    onClick={handleSaveClick}
                    label={t('Save bid')}
                    variant="edit"
                    disabled={!canSaveForSelectedDate}
                    loading={isSaving}
                  />
                </span>
              </Tooltip>
            )}
            {isLocked ? (
              <div className={styles.MFRRCMTrading_UnlockButton}>
                <TooltipIconButton
                  onClick={handleUnlockClick}
                  icon="fal fa-lock"
                  tooltip={t('Unlock bid')}
                  iconColor={colors.primary5}
                  disabled={isDateTooFarInFuture}
                />
              </div>
            ) : (
              <Tooltip title={!canLockForSelectedDate ? t('No values to be locked') : ''} arrow>
                <span>
                  <NewButton
                    onClick={handleLockClick}
                    label={t('Lock bid')}
                    variant="edit"
                    disabled={!canLockForSelectedDate}
                    loading={publishMutation.isPending}
                    style={
                      canLockForSelectedDate
                        ? {
                            backgroundColor: colors.warning4,
                            color: colors.black,
                          }
                        : undefined
                    }
                  />
                </span>
              </Tooltip>
            )}
          </div>
        )}
        {bidStatusInfo ? (
          <p className={styles.MFRRCMTrading_StatusText}>
            {t(bidStatusInfo.isLocked ? 'Locked by {{name}} at {{time}}' : 'Last saved by {{name}} at {{time}}', {
              name: bidStatusInfo.name,
              time: bidStatusInfo.time,
            })}
          </p>
        ) : null}
        <MfrrLastDeliveredStatus
          systemId={systemId}
          market={EAM_MARKET}
          units={units}
          dateStartTime={dateStartTime}
          dateEndTime={dateEndTime}
          isDeadlinePassed={!hasEditableEamRows}
        />
        {hasDraftBidsForDeliveredDate && isEamDeadlinePassed && (
          <div className="w-fit">
            <InfoBanner
              style="warning"
              text={t(
                'This date has delivered bids. Your unpublished changes will not be in effect until you publish them again.'
              )}
            />
          </div>
        )}
        <div className={`${styles.MFRRCMTrading_Row} ${styles.MFRRCMTrading_Content}`}>
          {isConfigLoading ? <p>{t('Loading mFRR-EAM configuration...')}</p> : null}
          {hasConfigError ? <InfoBanner style="danger" text={t('Failed to load mFRR-EAM configuration.')} /> : null}
          {!isConfigLoading && !hasConfigError && units.length === 0 ? (
            <InfoBanner style="warning" text={t('No production units are configured for mFRR-EAM.')} />
          ) : null}
          {!isConfigLoading && !hasConfigError && units.length > 0 ? (
            <div className={styles.MFRRCMTrading_Row}>
              <MfrrBidTable
                market={EAM_MARKET}
                units={units}
                dateStartTime={dateStartTime}
                initialValues={tableInitialValues}
                publishedBids={publishedBids}
                clearEditableRowsSignal={dialogState.clearEditableRowsSignal}
                isReadOnly={isBidsLoading || isLocked || isDateTooFarInFuture}
                eamGateClosureMinutesBefore={mfrrConfig?.eam_gate_closure_minutes_before}
                eamInputMarginClosureTime={mfrrConfig?.eam_input_margin_closure_time}
                eamCopyFromSignal={eamCopyFromSignal}
                cmBidsForEamCopy={copySourceBidsForEam}
                isCmBidsForEamCopyPending={isCopySourceBidsForEamPending}
                onCopyFromFailure={handleCopyFromFailure}
                marketResultVolumesByUnit={marketResultVolumesByUnit}
                highlightedRowIndices={highlightedRowIndices}
                onEligibilityChange={setEligibility}
                onPayloadChange={setCurrentPayload}
              />
            </div>
          ) : null}
        </div>
      </Card>

      <ConfirmDialog
        open={dialogState.emptyTableConfirmOpen}
        onClose={() => dispatchDialogAction({ type: 'closeEmptyTableConfirm' })}
        onConfirm={handleEmptyTableConfirm}
        textObject={{
          title: t('Clear table'),
          text: t('Are you sure you want to clear all bids?'),
          confirm: t('Clear'),
          deny: t('Cancel'),
        }}
      />

      <ConfirmDialog
        open={dialogState.unlockConfirmOpen}
        onClose={() => dispatchDialogAction({ type: 'closeUnlockConfirm' })}
        onConfirm={handleUnlockConfirm}
        textObject={{
          title: t('Unlock bid'),
          text: t('Are you sure you want to edit/unlock these bids?'),
          confirm: t('Unlock'),
          deny: t('Cancel'),
        }}
      />

      <ConfirmDialog
        open={lockWarningViolations.length > 0}
        onClose={clearLockFeedback}
        onConfirm={publishBids}
        fullWidth
        maxWidth="lg"
        alternativeButton={{ text: 'Cancel and mark rows', onClick: handleCancelAndMarkRows }}
        textObject={{
          title: t('Lock bid'),
          text: t('One or more bids have a volume lower than the accepted market results. Do you want to lock anyway?'),
          confirm: t('Lock'),
          deny: t('Cancel'),
        }}
      >
        <MfrrLockWarningTable violations={lockWarningViolations} units={units} dateStartTime={dateStartTime} />
      </ConfirmDialog>
    </>
  )
}
